import React, { useState } from 'react';
import { observer } from 'mobx-react';
import { Dropdown } from 'semantic-ui-react';
import { Placeholder } from '../../pages/MyMeetingsPage.styles';
import useDownloadMurphyReport from '../MurphyReport/useDownloadMurphyReport';

const dropdownStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '8px 16px',
  borderRadius: 6,
  border: '1px solid #0784a7',
  background: '#0784a7',
  color: '#ffffff',
  fontFamily: 'inherit',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

// Outline variant of the same visual language as the download dropdown — same
// shape and footprint, but reads as a secondary action. minHeight + padding
// tuned to match Semantic UI Dropdown's intrinsic height so the two buttons
// line up flush.
const shareButtonStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  padding: '10px 16px',
  minHeight: 38,
  lineHeight: '1.21428571em',
  borderRadius: 6,
  border: '1px solid #0784a7',
  background: '#ffffff',
  color: '#0784a7',
  fontFamily: 'inherit',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
};

const shareButtonCopiedStyle = {
  ...shareButtonStyle,
  background: '#10b981',
  border: '1px solid #10b981',
  color: '#ffffff',
};

const shareButtonDisabledStyle = {
  ...shareButtonStyle,
  background: '#f1f5f9',
  border: '1px solid #cbd5e1',
  color: '#94a3b8',
  cursor: 'not-allowed',
};

// Standalone dropdown for the Meeting Details row. Triggers a client-side PDF
// generation via @react-pdf and downloads to the user's machine.
export const DownloadReportButton = observer(({ meeting }) => {
  const { download, generating, error } = useDownloadMurphyReport();

  if (meeting.status !== 'delivered') return null;
  const assessment = meeting.assessment;
  if (!assessment) return null;

  const onSelect = (_e, { value }) => {
    if (generating) return;
    download({ meeting, assessment, variant: value });
  };

  return (
    <div>
      <Dropdown
        text={generating ? 'Preparing PDF…' : 'Download report'}
        disabled={generating}
        style={dropdownStyle}
        onChange={onSelect}
        selectOnBlur={false}
        value={null}
        options={[
          { key: 'internal', text: 'Internal review (for you)', value: 'internal' },
          { key: 'shared',   text: 'Shared with prospect',      value: 'shared'   },
        ]}
      />
      {error && <Placeholder style={{ marginTop: 4 }}>Couldn't generate: {error}</Placeholder>}
    </div>
  );
});

export default DownloadReportButton;

// Sibling action: copies a public no-auth share URL to the user's clipboard.
// Recipients open the link in any browser without logging in — they see a
// neutral summary, key points, and next steps. Shows only for delivered
// meetings whose assessment already has a shareToken minted by the analyzer.
export const ShareSummaryButton = observer(({ meeting }) => {
  const [state, setState] = useState({ status: 'idle', message: '' });

  if (meeting.status !== 'delivered') return null;
  const assessment = meeting.assessment;
  if (!assessment) return null;

  const hasToken = !!assessment.shareToken;
  const buttonStyle = state.status === 'copied' ? shareButtonCopiedStyle
                    : !hasToken ? shareButtonDisabledStyle
                    : shareButtonStyle;
  const label = state.status === 'copied' ? 'Copied ✓'
              : !hasToken ? 'Share unavailable'
              : 'Share summary';

  const handleClick = async () => {
    if (!hasToken) {
      setState({ status: 'unavailable', message: 'Re-analyze this meeting to enable sharing.' });
      setTimeout(() => setState({ status: 'idle', message: '' }), 4000);
      return;
    }
    const shareUrl = `${window.location.origin}/share/${assessment.shareToken}`;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setState({ status: 'copied', message: 'Share link copied — valid for 90 days. Paste anywhere; no login required for the recipient.' });
      setTimeout(() => setState({ status: 'idle', message: '' }), 4000);
    } catch (err) {
      console.error('ShareSummaryButton: clipboard write failed', err);
      setState({ status: 'error', message: 'Could not copy to clipboard.' });
      setTimeout(() => setState({ status: 'idle', message: '' }), 4000);
    }
  };

  return (
    <div>
      <button
        data-testid='share-summary'
        type='button'
        onClick={handleClick}
        style={buttonStyle}
        disabled={state.status === 'copied'}
      >
        {label}
      </button>
      {state.message && (
        <Placeholder style={{ marginTop: 4, color: state.status === 'error' ? '#db2828' : undefined }}>
          {state.message}
        </Placeholder>
      )}
    </div>
  );
});

